import React, { useState } from 'react';
import Layout from '../components/Layout';
import Button from '../components/Button';
import MetaTags from '../components/MetaTags';
import api from '../utils/api';
import { FaClock, FaCheckCircle, FaTv, FaUsers, FaChartLine, FaBook } from 'react-icons/fa';

const FreeRundownCreator = () => {
  const [inputText, setInputText] = useState('');
  const [targetLanguage, setTargetLanguage] = useState('english');
  const [orientation, setOrientation] = useState('portrait');
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Column headers mapping
  const columnHeaders = {
    english: {
      time: 'Time',
      duration: 'Duration',
      title: 'Segment / Title',
      on_screen: 'On Screen',
      graphics: 'Graphics',
      notes: 'Notes'
    },
    swedish: {
      time: 'Tid',
      duration: 'Längd',
      title: 'Moment / Titel',
      on_screen: 'Medverkande i bild',
      graphics: 'Bild / Grafik',
      notes: 'Notiser'
    }
  };

  const getHeaders = () => {
    const lang = targetLanguage.toLowerCase();
    return columnHeaders[lang] || columnHeaders.english;
  };

  const handleGenerate = async () => {
    if (!inputText.trim()) {
      setError('Please enter some text to generate a rundown.');
      return;
    }

    // Track event
    if (window.umami) {
      window.umami.track('Rundown Generated', { language: targetLanguage });
    }

    setLoading(true);
    setError(null);
    setSchedule(null);

    try {
      const response = await api.post('/api/rundown/schedule', {
        text: inputText,
        target_language: targetLanguage
      });

      // Validate response
      if (response.data && response.data.schedule && Array.isArray(response.data.schedule)) {
        setSchedule(response.data.schedule);
      } else {
        throw new Error('Invalid response format from server');
      }
    } catch (err) {
      console.error('Error generating schedule:', err);
      setError(err.response?.data?.error || err.message || 'Failed to generate rundown. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleExportDOCX = async () => {
    if (!schedule) return;

    // Track event
    if (window.umami) {
      window.umami.track('Export DOCX', { orientation, language: targetLanguage });
    }

    console.log('Exporting DOCX with schedule:', {
      scheduleType: typeof schedule,
      isArray: Array.isArray(schedule),
      scheduleLength: schedule?.length,
      schedule: schedule
    });

    setExporting(true);
    try {
      const response = await api.post('/api/rundown/export/docx', {
        schedule,
        headers: getHeaders(),
        orientation
      }, {
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `rundown-${Date.now()}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting DOCX:', err);
      const errorMessage = err.response?.data?.error || 'Failed to export DOCX';
      const errorDetails = err.response?.data?.details;
      setError(`${errorMessage}${errorDetails ? ': ' + errorDetails : ''}. Please try again.`);
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async () => {
    if (!schedule) return;

    // Track event
    if (window.umami) {
      window.umami.track('Export PDF', { orientation, language: targetLanguage });
    }

    console.log('Exporting PDF with schedule:', {
      scheduleType: typeof schedule,
      isArray: Array.isArray(schedule),
      scheduleLength: schedule?.length,
      schedule: schedule
    });

    setExporting(true);
    try {
      const response = await api.post('/api/rundown/export/pdf', {
        schedule,
        headers: getHeaders(),
        orientation
      }, {
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], { 
        type: 'application/pdf' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `rundown-${Date.now()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting PDF:', err);
      const errorMessage = err.response?.data?.error || 'Failed to export PDF';
      const errorDetails = err.response?.data?.details;
      setError(`${errorMessage}${errorDetails ? ': ' + errorDetails : ''}. Please try again.`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Layout>
      <MetaTags 
        title="Free Broadcast Rundown Creator | Run of Show Generator | AskTide"
        description="Create professional broadcast rundowns and run of show schedules with AI. Used by TV networks, event producers, and live streamers. Free rundown generator with DOCX/PDF export. Transform unstructured notes into production-ready schedules."
        url={window.location.href}
      />
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Free Rundown Creator</h1>
          <p className="text-lg text-gray-600">
            Transform unstructured text into a professional broadcast rundown schedule
          </p>
        </div>

        {/* Input Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="mb-4">
            <label htmlFor="inputText" className="block text-sm font-medium text-gray-700 mb-2">
              Input Text
            </label>
            <textarea
              id="inputText"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="Paste your unstructured text here (any language)..."
              className="w-full h-64 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            />
          </div>

          <div className="flex items-center gap-4 mb-4">
            <div className="flex-1">
              <label htmlFor="targetLanguage" className="block text-sm font-medium text-gray-700 mb-2">
                Target Output Language
              </label>
              <input
                id="targetLanguage"
                type="text"
                value={targetLanguage}
                onChange={(e) => setTargetLanguage(e.target.value)}
                placeholder="e.g., english, swedish, spanish..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex-1">
              <label htmlFor="orientation" className="block text-sm font-medium text-gray-700 mb-2">
                Document Orientation
              </label>
              <select
                id="orientation"
                value={orientation}
                onChange={(e) => setOrientation(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="portrait">Portrait</option>
                <option value="landscape">Landscape</option>
              </select>
            </div>
            <div className="pt-7">
              <Button
                onClick={handleGenerate}
                disabled={loading || !inputText.trim()}
                className="px-8 py-2"
              >
                {loading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating...
                  </span>
                ) : (
                  'Generate Rundown'
                )}
              </Button>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}
        </div>

        {/* Schedule Preview */}
        {schedule && schedule.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-900">Schedule Preview</h2>
              <div className="flex gap-3">
                <Button
                  onClick={handleExportDOCX}
                  disabled={exporting}
                  variant="secondary"
                  className="flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  {exporting ? 'Exporting...' : 'Export DOCX'}
                </Button>
                <Button
                  onClick={handleExportPDF}
                  disabled={exporting}
                  variant="secondary"
                  className="flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  {exporting ? 'Exporting...' : 'Export PDF'}
                </Button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr style={{ backgroundColor: '#00274D', color: 'white' }}>
                    <th className="px-4 py-3 text-left font-bold">{getHeaders().time}</th>
                    <th className="px-4 py-3 text-left font-bold">{getHeaders().duration}</th>
                    <th className="px-4 py-3 text-left font-bold">{getHeaders().title}</th>
                    <th className="px-4 py-3 text-left font-bold">{getHeaders().on_screen}</th>
                    <th className="px-4 py-3 text-left font-bold">{getHeaders().graphics}</th>
                    <th className="px-4 py-3 text-left font-bold">{getHeaders().notes}</th>
                  </tr>
                </thead>
                <tbody>
                  {schedule.map((item, index) => (
                    <tr
                      key={index}
                      style={{
                        backgroundColor: index % 2 === 0 ? '#EAF2F8' : 'white'
                      }}
                    >
                      <td className="px-4 py-3 border border-gray-300">{item.time}</td>
                      <td className="px-4 py-3 border border-gray-300">{item.duration}</td>
                      <td className="px-4 py-3 border border-gray-300">{item.title}</td>
                      <td className="px-4 py-3 border border-gray-300">
                        {Array.isArray(item.on_screen) ? item.on_screen.join(', ') : item.on_screen}
                      </td>
                      <td className="px-4 py-3 border border-gray-300">{item.graphics}</td>
                      <td className="px-4 py-3 border border-gray-300">{item.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* How it works section */}
        <div className="bg-blue-50 rounded-lg p-6 mt-8">
          <h3 className="text-lg font-bold text-gray-900 mb-3">How it works</h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>Paste your unstructured text (meeting notes, rough draft, any format)</li>
            <li>Choose your target output language (defaults to English)</li>
            <li>Click "Generate Rundown" - AI will extract and structure your schedule</li>
            <li>Review the professional table with time, duration, segments, participants, graphics, and notes</li>
            <li>Export as DOCX or PDF with color-coded formatting</li>
          </ol>
        </div>

        {/* Educational Content Section - SEO Optimized */}
        <div className="mt-16 space-y-12">
          {/* What is a Rundown */}
          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">What is a Broadcast Rundown?</h2>
            <div className="bg-white rounded-lg shadow-sm p-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                A <strong>broadcast rundown</strong>, also known as a <strong>run of show</strong> or <strong>show rundown</strong>, is a detailed minute-by-minute schedule that outlines every element of a live production, broadcast, or event. It serves as the master plan that keeps your entire production team synchronized and ensures smooth execution from start to finish.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Professional rundowns typically include critical information such as precise timing, segment titles, on-screen talent, graphics cues, technical notes, and duration for each element. This comprehensive document transforms from planning chaos into coordinated excellence, making it an indispensable tool in television broadcasting, live streaming, corporate events, conferences, and webinars.
              </p>
            </div>
          </section>

          {/* Why Professionals Use Rundowns */}
          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Why Professional Broadcasters Rely on Rundowns</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6">
                <div className="flex items-start space-x-3">
                  <FaClock className="text-blue-600 text-2xl mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Precise Timing Control</h3>
                    <p className="text-gray-700">
                      Every second counts in live broadcasting. Rundowns provide exact timing for each segment, helping directors and producers stay on schedule and hit critical broadcast windows with precision.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6">
                <div className="flex items-start space-x-3">
                  <FaUsers className="text-green-600 text-2xl mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Team Coordination</h3>
                    <p className="text-gray-700">
                      A rundown serves as a single source of truth for your entire crew—from camera operators to graphics technicians. Everyone knows exactly what happens when, eliminating confusion and miscommunication.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-6">
                <div className="flex items-start space-x-3">
                  <FaTv className="text-purple-600 text-2xl mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Professional Quality</h3>
                    <p className="text-gray-700">
                      Well-organized rundowns are the hallmark of professional productions. Major networks like CNN, BBC, and NBC use detailed rundowns for every broadcast, ensuring consistent quality and smooth transitions.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-6">
                <div className="flex items-start space-x-3">
                  <FaChartLine className="text-orange-600 text-2xl mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Efficiency & Productivity</h3>
                    <p className="text-gray-700">
                      Rundowns reduce setup time, minimize errors, and allow for quick adjustments. When everyone works from the same schedule, productions run smoother and teams work more efficiently.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Benefits Section */}
          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Key Benefits of Using Production Rundowns</h2>
            <div className="bg-white rounded-lg shadow-sm p-8">
              <div className="grid md:grid-cols-3 gap-8">
                <div>
                  <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                    <FaCheckCircle className="text-blue-600 text-xl" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Reduced Stress</h3>
                  <p className="text-gray-600 text-sm">
                    Clear documentation eliminates last-minute panic and gives your team confidence in execution.
                  </p>
                </div>
                <div>
                  <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                    <FaCheckCircle className="text-green-600 text-xl" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Better Communication</h3>
                  <p className="text-gray-600 text-sm">
                    Standardized format ensures everyone speaks the same language and understands their role.
                  </p>
                </div>
                <div>
                  <div className="bg-purple-100 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                    <FaCheckCircle className="text-purple-600 text-xl" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Easy Revisions</h3>
                  <p className="text-gray-600 text-sm">
                    Make real-time changes and distribute updates instantly to keep everyone aligned.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Industry Use Cases */}
          <section className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Who Uses Broadcast Rundowns?</h2>
            <div className="space-y-4">
              <div className="bg-white rounded-lg p-5 shadow-sm">
                <h3 className="font-bold text-gray-900 mb-2">📺 Television Networks & News Programs</h3>
                <p className="text-gray-700 text-sm">
                  Every news broadcast, morning show, and live television production uses detailed rundowns to coordinate anchors, reporters, graphics, and commercial breaks. Networks like ESPN, FOX News, and CBS rely on rundowns for every show.
                </p>
              </div>
              <div className="bg-white rounded-lg p-5 shadow-sm">
                <h3 className="font-bold text-gray-900 mb-2">🎥 Live Events & Conferences</h3>
                <p className="text-gray-700 text-sm">
                  Corporate events, product launches, conferences, and award shows use rundowns to manage speakers, presentations, video playback, and technical cues. Companies like Apple and Microsoft use detailed rundowns for keynote presentations.
                </p>
              </div>
              <div className="bg-white rounded-lg p-5 shadow-sm">
                <h3 className="font-bold text-gray-900 mb-2">🎙️ Podcasts & Streaming Shows</h3>
                <p className="text-gray-700 text-sm">
                  Professional podcasters and live streamers use rundowns to structure episodes, plan segments, coordinate guests, and ensure smooth content flow. Popular shows maintain quality through consistent rundown usage.
                </p>
              </div>
              <div className="bg-white rounded-lg p-5 shadow-sm">
                <h3 className="font-bold text-gray-900 mb-2">🏢 Corporate Communications</h3>
                <p className="text-gray-700 text-sm">
                  Town halls, investor calls, training sessions, and webinars benefit from structured rundowns that keep executives on track and ensure all talking points are covered within time constraints.
                </p>
              </div>
            </div>
          </section>

          {/* Best Practices */}
          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Best Practices for Creating Effective Rundowns</h2>
            <div className="bg-white rounded-lg shadow-sm p-8">
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0">1</div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Start with Accurate Timing</h3>
                    <p className="text-gray-700">
                      Include both clock time (8:00 AM) and duration (3:00 minutes) for each segment. Account for buffers and transitions to avoid running over.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0">2</div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Be Specific with Details</h3>
                    <p className="text-gray-700">
                      Include names of on-screen talent, specific graphics files, camera positions, and technical cues. The more detail, the smoother the execution.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0">3</div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Use Consistent Formatting</h3>
                    <p className="text-gray-700">
                      Standardize your rundown format across all productions. Consistency helps team members quickly scan and understand the schedule.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0">4</div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Distribute Early & Update Often</h3>
                    <p className="text-gray-700">
                      Share the rundown with your team well in advance. As changes occur, distribute updated versions with clear version numbers or timestamps.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* SEO-Rich FAQ Section */}
          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Frequently Asked Questions About Broadcast Rundowns</h2>
            <div className="space-y-4">
              <details className="bg-white rounded-lg shadow-sm">
                <summary className="px-6 py-4 cursor-pointer font-semibold text-gray-900 hover:text-blue-600">
                  What's the difference between a rundown and a script?
                </summary>
                <div className="px-6 pb-4 text-gray-700">
                  A rundown is a timing and logistics document that outlines what happens when, while a script contains the actual words spoken by talent. The rundown references the script but focuses on the overall show flow, technical cues, and timing.
                </div>
              </details>
              <details className="bg-white rounded-lg shadow-sm">
                <summary className="px-6 py-4 cursor-pointer font-semibold text-gray-900 hover:text-blue-600">
                  How detailed should a production rundown be?
                </summary>
                <div className="px-6 pb-4 text-gray-700">
                  The level of detail depends on your production complexity. At minimum, include timing, segment titles, and key participants. More complex productions should add technical cues, graphics references, camera shots, and detailed notes for each team member.
                </div>
              </details>
              <details className="bg-white rounded-lg shadow-sm">
                <summary className="px-6 py-4 cursor-pointer font-semibold text-gray-900 hover:text-blue-600">
                  Can I create a rundown without specialized software?
                </summary>
                <div className="px-6 pb-4 text-gray-700">
                  Yes! While professional broadcast control rooms use specialized software, you can create effective rundowns using spreadsheets, word processors, or tools like our Free Rundown Creator. The format matters more than the tool—as long as information is clear and accessible to your team.
                </div>
              </details>
              <details className="bg-white rounded-lg shadow-sm">
                <summary className="px-6 py-4 cursor-pointer font-semibold text-gray-900 hover:text-blue-600">
                  How far in advance should I create a rundown?
                </summary>
                <div className="px-6 pb-4 text-gray-700">
                  Create your initial rundown as early as possible—ideally several days to a week before the event. This gives team members time to review, prepare, and raise questions. Expect to make revisions, especially in the 24-48 hours leading up to the broadcast.
                </div>
              </details>
            </div>
          </section>

          {/* Recommended Resources */}
          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Recommended Rundown Tools & Resources</h2>
            <p className="text-gray-700 mb-6">
              While our free rundown creator is perfect for getting started, here are some professional tools and resources for advanced production workflows:
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <a 
                href="https://rundownstudio.app/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow border-2 border-transparent hover:border-blue-500"
                data-umami-event="External Link - Rundown Studio"
              >
                <h3 className="text-xl font-bold text-gray-900 mb-2">Rundown Studio</h3>
                <p className="text-gray-700 text-sm mb-3">
                  Professional rundown software with real-time collaboration, automatic timing calculations, and built-in teleprompter. Used by esports productions, live streamers, and conference organizers.
                </p>
                <p className="text-blue-600 text-sm font-semibold">Visit Rundown Studio →</p>
              </a>

              <a 
                href="https://www.shoflo.tv/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow border-2 border-transparent hover:border-blue-500"
                data-umami-event="External Link - Shoflo"
              >
                <h3 className="text-xl font-bold text-gray-900 mb-2">Shoflo</h3>
                <p className="text-gray-700 text-sm mb-3">
                  Cloud-based rundown and production management platform used by major broadcasters and live event producers. Features advanced collaboration tools and mobile apps for crew coordination.
                </p>
                <p className="text-blue-600 text-sm font-semibold">Explore Shoflo →</p>
              </a>

              <a 
                href="https://www.rossvideo.com/products-services/production-technology/rundown-graphics-control/overdrive/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow border-2 border-transparent hover:border-blue-500"
                data-umami-event="External Link - Ross OverDrive"
              >
                <h3 className="text-xl font-bold text-gray-900 mb-2">Ross OverDrive</h3>
                <p className="text-gray-700 text-sm mb-3">
                  Enterprise-grade automated production control system for television studios. Integrates rundowns with graphics, video servers, and robotic cameras for major broadcast operations.
                </p>
                <p className="text-blue-600 text-sm font-semibold">Learn about OverDrive →</p>
              </a>

              <a 
                href="https://www.studiobinder.com/blog/what-is-a-rundown/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow border-2 border-transparent hover:border-blue-500"
                data-umami-event="External Link - StudioBinder Guide"
              >
                <h3 className="text-xl font-bold text-gray-900 mb-2">StudioBinder Rundown Guide</h3>
                <p className="text-gray-700 text-sm mb-3">
                  Comprehensive educational resource about creating production rundowns. Includes templates, examples, and best practices from the film and television industry.
                </p>
                <p className="text-blue-600 text-sm font-semibold">Read the guide →</p>
              </a>
            </div>
            <div className="mt-6 bg-blue-50 rounded-lg p-6">
              <p className="text-sm text-gray-700">
                <strong>Note:</strong> These are independent resources and tools. We recommend exploring different options to find what best fits your production needs and budget. Our free rundown creator is ideal for getting started or for occasional use, while these professional tools offer advanced features for regular production workflows.
              </p>
            </div>
          </section>

          {/* Call to Action */}
          <section className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg p-8 text-white text-center">
            <FaBook className="text-4xl mx-auto mb-4 opacity-90" />
            <h2 className="text-2xl font-bold mb-4">Start Creating Professional Rundowns Today</h2>
            <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
              Whether you're producing a live newscast, hosting a corporate webinar, or planning a complex conference, our AI-powered rundown creator transforms your unstructured notes into professional broadcast schedules in seconds.
            </p>
            <p className="text-sm text-blue-200">
              Join thousands of broadcasters, producers, and event managers who trust AskTide for professional production planning.
            </p>
          </section>
        </div>
      </div>
    </Layout>
  );
};

export default FreeRundownCreator;

